import React from "react";
import { usePlayer } from "@/context/PlayerContext";
import { usePurchase } from "@/context/PurchaseContext";
import { HeartButton } from "@/components/HeartButton";
import { API } from "@/lib/api";
import { MiniWaveform } from "@/components/MiniWaveform";
import { IncludedFormats } from "@/components/IncludedFormats";
import { Play, Pause, ShoppingBag, Check, Loader2, Headphones, Star } from "lucide-react";

const CompactBeat = ({ track, list, owned }) => {
  const { current, isPlaying, playTrack } = usePlayer();
  const { openBuy, loadingId } = usePurchase();
  const isCurrent = current?.id === track.id;
  const loading = loadingId === track.id;

  return (
    <div data-testid={`beat-${track.id}`}
      className="group flex items-center gap-3 bg-[#0f0f0f] border border-[#1e1e1e] rounded-xl p-2.5 hover:border-[#e2ff31]/40 transition-colors">
      <div className="relative w-11 h-11 shrink-0">
        <img src={`${API}${track.cover_url}`} alt={track.title}
          className={`w-full h-full rounded-lg object-cover ${track.sold ? "opacity-50" : ""}`} />
        <button data-testid={`play-track-${track.id}`} onClick={() => playTrack(track, list)}
          className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ opacity: isCurrent ? 1 : undefined }}>
          {isCurrent && isPlaying ? <Pause className="w-4 h-4 text-[#e2ff31]" fill="currentColor" />
            : <Play className="w-4 h-4 text-white ml-0.5" fill="currentColor" />}
        </button>
      </div>

      <div className="min-w-0 flex-1">
        <p className={`text-sm font-medium truncate flex items-center gap-1.5 ${isCurrent ? "text-[#e2ff31]" : ""} ${track.sold ? "line-through opacity-60" : ""}`}>
          {track.featured && !track.sold && (
            <Star data-testid={`featured-badge-${track.id}`} className="w-3.5 h-3.5 text-[#e2ff31] shrink-0" fill="currentColor" />
          )}
          <span className="truncate">{track.title}</span>
        </p>
        <div className="flex items-center gap-2 text-xs text-[#6b7280]">
          <span className="truncate">{track.producer}</span>
          <span className="flex items-center gap-0.5 font-mono shrink-0"><Headphones className="w-3 h-3" /> {(track.play_count || 0).toLocaleString()}</span>
          {track.bpm ? <span className="font-mono shrink-0">{track.bpm}bpm</span> : null}
          {!track.sold && <IncludedFormats track={track} className="text-[10px] shrink-0" />}
        </div>
        {track.waveform && track.waveform.length ? (
          <MiniWaveform peaks={track.waveform} bars={40} active={isCurrent} className="h-4 mt-1.5" />
        ) : null}
      </div>

      {owned ? (
        <span className="flex items-center gap-1 text-xs text-[#22c55e] font-medium px-2 shrink-0"><Check className="w-3.5 h-3.5" /> Owned</span>
      ) : track.sold ? (
        <span data-testid={`sold-badge-${track.id}`} className="text-xs font-bold text-[#ef4444] border border-[#ef4444]/40 rounded-full px-3 py-1.5 uppercase shrink-0">Sold</span>
      ) : (
        <div className="flex items-center gap-1 shrink-0">
          <HeartButton track={track} />
          <button data-testid={`buy-track-${track.id}`} onClick={() => openBuy(track)} disabled={loading}
            className="flex items-center gap-1.5 rounded-full bg-[#e2ff31] text-black hover:bg-[#c8e619] active:scale-95 transition-all font-semibold text-xs px-3 py-1.5">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShoppingBag className="w-3.5 h-3.5" />}
            ${track.price?.toFixed(2)}
          </button>
        </div>
      )}
    </div>
  );
};

export const GenreColumns = ({ tracks, owned, preserveOrder = false }) => {
  const groups = {};
  for (const t of tracks) {
    const g = t.genre || "Other";
    (groups[g] = groups[g] || []).push(t);
  }
  // Float the featured beat to the top of its genre column, unless a sort is active.
  if (!preserveOrder) {
    for (const g of Object.keys(groups)) {
      groups[g].sort((a, b) => (b.featured && !b.sold ? 1 : 0) - (a.featured && !a.sold ? 1 : 0));
    }
  }
  const genres = Object.keys(groups).sort((a, b) => groups[b].length - groups[a].length);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="genre-columns">
      {genres.map((genre) => (
        <div key={genre} data-testid={`genre-col-${genre}`} className="bg-[#121212]/60 border border-[#2a2a2a] rounded-2xl p-4">
          <div className="flex items-center justify-between mb-4 px-1">
            <h3 className="font-heading text-lg font-semibold tracking-tight">{genre}</h3>
            <span className="text-xs font-mono text-[#6b7280] bg-[#1e1e1e] rounded-full px-2.5 py-1">{groups[genre].length}</span>
          </div>
          <div className="space-y-2">
            {groups[genre].map((t) => (
              <CompactBeat key={t.id} track={t} list={groups[genre]} owned={owned.has(t.id)} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
